/**
 * LEVOR Remote Desktop Web Client - Main Entry Point
 */

import './styles/main.css';

import { RemoteDesktopClient } from './components/RemoteDesktopClient';
import { Toolbar } from './components/Toolbar';
import { LoginDialog, Toast } from './components/LoginDialog';
import { AISidebar } from './components/AISidebar';
import { FileTransferPanel } from './components/FileTransferPanel';
import { ConnectionStatus, AgentInfo } from './types';

// ============================================================================
// Connection History (localStorage)
// ============================================================================

interface RecentConnection {
    agentId: string;
    machineName: string;
    lastConnected: string; // ISO date
    isFavorite: boolean;
}

const HISTORY_KEY = 'levor_recent_connections';
const MAX_HISTORY = 10;

function getConnectionHistory(): RecentConnection[] {
    try {
        return JSON.parse(localStorage.getItem(HISTORY_KEY) || '[]');
    } catch { return []; }
}

function saveConnectionToHistory(agentId: string, machineName: string): void {
    const history = getConnectionHistory();
    const existing = history.find(h => h.agentId === agentId);
    if (existing) {
        existing.lastConnected = new Date().toISOString();
        existing.machineName = machineName;
    } else {
        history.unshift({ agentId, machineName, lastConnected: new Date().toISOString(), isFavorite: false });
    }
    // Sort: favorites first, then by date
    history.sort((a, b) => {
        if (a.isFavorite !== b.isFavorite) return a.isFavorite ? -1 : 1;
        return new Date(b.lastConnected).getTime() - new Date(a.lastConnected).getTime();
    });
    localStorage.setItem(HISTORY_KEY, JSON.stringify(history.slice(0, MAX_HISTORY)));
}

function toggleFavorite(agentId: string): void {
    const history = getConnectionHistory();
    const entry = history.find(h => h.agentId === agentId);
    if (entry) {
        entry.isFavorite = !entry.isFavorite;
        localStorage.setItem(HISTORY_KEY, JSON.stringify(history));
    }
}

// ============================================================================
// Application State
// ============================================================================

interface AppState {
    isInitialized: boolean;
    isAuthenticated: boolean;
    selectedAgentId: string | null;
}

const state: AppState = {
    isInitialized: false,
    isAuthenticated: false,
    selectedAgentId: null
};

// ============================================================================
// Services
// ============================================================================

let client: RemoteDesktopClient;
let toolbar: Toolbar;
let loginDialog: LoginDialog;
let toast: Toast;
let aiSidebar: AISidebar;
let fileTransferPanel: FileTransferPanel;

// ============================================================================
// Configuration
// ============================================================================

const CONFIG = {
    // Server URL - can be overridden by environment
    serverUrl: import.meta.env.VITE_SERVER_URL || getDefaultServerUrl(),

    // Features
    enableP2P: true,
    enableEncryption: true,
    autoReconnect: true,
    version: '1.4.0-build2'
};

function getDefaultServerUrl(): string {
    // Use current origin in production, localhost in development
    return window.location.hostname === 'localhost'
        ? 'http://localhost:5050'
        : window.location.origin;
}

// ============================================================================
// Initialization
// ============================================================================

async function initializeApp(): Promise<void> {
    console.log('Initializing LEVOR Web Client...');

    // Initialize toast notifications
    toast = new Toast();

    // Get DOM elements
    const app = document.getElementById('app');
    if (!app) {
        throw new Error('App container not found');
    }

    // Render initial HTML structure
    app.innerHTML = `
        <div id="toolbar-container"></div>
        <div class="canvas-container" id="canvas-container">
            <canvas id="remote-canvas" width="1920" height="1080"></canvas>
            <div class="welcome-screen" id="welcome-screen">
                <div class="welcome-icon">🖥️</div>
                <h1 class="welcome-title">LEVOR Remote Desktop</h1>
                <p class="welcome-subtitle">שליטה מרחוק במחשבים בקלות ובמהירות</p>

                <!-- Connection Status Banner -->
                <div class="connection-status-banner" id="connection-status-banner">
                    <span class="csb-dot"></span>
                    <span class="csb-text">מנותק</span>
                </div>

                <!-- Quick Connect Panel (ID + Password) -->
                <div class="quick-connect-panel" id="quick-connect-panel">
                    <h3 class="qc-title">חיבור מהיר</h3>
                    <div class="qc-form">
                        <div class="qc-field">
                            <label for="qc-agent-id">מזהה מחשב</label>
                            <input type="text" id="qc-agent-id" placeholder="לדוג': ABC123DEF456"
                                   autocomplete="off" spellcheck="false" dir="ltr" />
                        </div>
                        <div class="qc-field">
                            <label for="qc-password">סיסמה</label>
                            <input type="password" id="qc-password" placeholder="הכנס סיסמה"
                                   autocomplete="off" />
                        </div>
                        <button id="qc-connect-btn" class="qc-btn" disabled>
                            <span class="qc-btn-icon">🔗</span>
                            <span class="qc-btn-text">התחבר</span>
                        </button>
                    </div>
                    <div id="qc-error" class="qc-error" style="display:none;"></div>
                    <div class="qc-divider"><span>או</span></div>
                    <p class="qc-hint">בחר מחשב מהרשימה בסרגל הכלים למעלה</p>
                    <div id="recent-connections" class="recent-connections"></div>
                </div>

                <div class="welcome-features">
                    <div class="feature-item">
                        <span class="feature-icon">⚡</span>
                        <span class="feature-text">חיבור P2P מהיר</span>
                    </div>
                    <div class="feature-item">
                        <span class="feature-icon">🔒</span>
                        <span class="feature-text">הצפנה מקצה לקצה</span>
                    </div>
                    <div class="feature-item">
                        <span class="feature-icon">🌐</span>
                        <span class="feature-text">עובד בכל דפדפן</span>
                    </div>
                </div>
            </div>
            <div class="connecting-overlay" id="connecting-overlay" style="display: none;">
                <div class="spinner-large"></div>
                <div class="connecting-text">מתחבר...</div>
            </div>
        </div>
    `;

    const canvas = document.getElementById('remote-canvas') as HTMLCanvasElement;
    const toolbarContainer = document.getElementById('toolbar-container') as HTMLElement;

    // Initialize Remote Desktop Client
    client = new RemoteDesktopClient({
        serverUrl: CONFIG.serverUrl,
        canvas: canvas,
        autoReconnect: CONFIG.autoReconnect,
        enableP2P: CONFIG.enableP2P,
        enableEncryption: CONFIG.enableEncryption
    }, {
        onConnectionStatusChange: handleConnectionStatusChange,
        onAgentListUpdated: handleAgentListUpdated,
        onSessionStarted: handleSessionStarted,
        onSessionEnded: handleSessionEnded,
        onVideoStatsUpdate: (stats) => toolbar.updateVideoStats(stats),
        onNetworkStatsUpdate: (stats) => toolbar.updateNetworkStats(stats),
        onMonitorChanged: (monitorIndex, width, height) => {
            toolbar.setMonitorSwitching(false);
            toast.show(`מסך ${monitorIndex >= 0 ? monitorIndex + 1 : 'כל המסכים'}: ${width}x${height}`, 'info');
        },
        onError: handleError,
        onLog: (msg) => console.log(msg)
    });

    // Initialize Toolbar
    toolbar = new Toolbar({ container: toolbarContainer }, {
        onAgentSelect: handleAgentSelect,
        onDisconnect: handleDisconnect,
        onMonitorSelect: handleMonitorSelect,
        onFullscreen: handleFullscreen,
        onSettingsChange: (settings) => client.updateSettings(settings),
        onAnalyzeScreen: handleAnalyzeScreen,
        onToggleAI: () => aiSidebar.toggle(),
        onFileTransferToggle: () => fileTransferPanel?.toggle()
    });

    // S2-7: Wire toolbar audio callbacks to client's AudioService
    toolbar.onVolumeChange = (volume: number) => {
        client.getAudioService()?.setVolume(volume);
    };
    toolbar.onMuteToggle = (muted: boolean) => {
        client.getAudioService()?.setMuted(muted);
    };

    // Initialize AI Sidebar
    aiSidebar = new AISidebar(document.body, CONFIG.serverUrl);

    // WC-8/11: Initialize File Transfer Panel (wired to toolbar button + progress)
    const ftService = client.getFileTransferService();
    fileTransferPanel = new FileTransferPanel({
        fileTransferService: ftService
    });
    // Wire progress events to panel UI via public callback
    ftService.onProgressExternal = (progress) => {
        fileTransferPanel?.updateTransferProgress(progress);
    };

    // Initialize Login Dialog
    loginDialog = new LoginDialog({
        onLogin: handleLogin,
        onCancel: () => console.log('Login cancelled')
    });

    // Quick Connect panel event handlers
    setupQuickConnect();

    state.isInitialized = true;
    console.log('App initialized');

    // Check authentication and connect
    await checkAuthAndConnect();
}

// ============================================================================
// Authentication
// ============================================================================

async function checkAuthAndConnect(): Promise<void> {
    // Check for stored JWT token from previous session
    const storedToken = localStorage.getItem('remotedesktop_auth_token');
    if (storedToken) {
        try {
            const tokenData = JSON.parse(storedToken);
            const expiresAt = new Date(tokenData.expiresAt);
            if (expiresAt > new Date()) {
                state.isAuthenticated = true;
                console.log('Restored authentication from stored token');
            }
        } catch { /* Invalid stored token, proceed unauthenticated */ }
    }

    try {
        await connectToServer();
    } catch (error) {
        // Connection failed — show login dialog for authenticated access
        // Quick Connect (ID+Password) still works without login
        console.log('Initial connection failed, showing login for account-based access');
        loginDialog.show();
    }
}

async function handleLogin(username: string, password: string): Promise<void> {
    try {
        const success = await client.login(username, password);

        if (success) {
            loginDialog.hide();
            state.isAuthenticated = true;
            toast.show('התחברת בהצלחה!', 'success');

            await connectToServer();
        } else {
            loginDialog.showError('שם משתמש או סיסמה שגויים');
        }
    } catch (error) {
        loginDialog.showError('שגיאה בהתחברות לשרת');
    }
}

async function connectToServer(retryCount: number = 0): Promise<void> {
    const MAX_RETRIES = 3;
    const retryText = retryCount > 0 ? ` (ניסיון ${retryCount + 1})` : '';
    showConnectingOverlay(true, `מתחבר לשרת...${retryText}`);

    try {
        await client.connect();
        toast.show('מחובר לשרת', 'success');
    } catch (error) {
        if (retryCount < MAX_RETRIES) {
            // Exponential backoff: 1s, 2s, 4s
            const delay = Math.pow(2, retryCount) * 1000;
            showConnectingOverlay(true, `שגיאה בחיבור. מנסה שוב בעוד ${delay / 1000} שניות...`);
            await new Promise(resolve => setTimeout(resolve, delay));
            return connectToServer(retryCount + 1);
        }
        toast.show('שגיאה בהתחברות לשרת. בדוק את החיבור לרשת.', 'error');
        showAIErrorResolution('Failed to connect to the remote desktop server after multiple attempts.');
        throw error;
    } finally {
        showConnectingOverlay(false);
    }
}

// ============================================================================
// Event Handlers
// ============================================================================

function handleConnectionStatusChange(status: ConnectionStatus): void {
    console.log('Connection status:', status);
    toolbar.updateStatus(status, client.isUsingP2P);
    updateConnectionBanner(status);

    const canvas = document.getElementById('remote-canvas') as HTMLCanvasElement;
    const welcomeScreen = document.getElementById('welcome-screen') as HTMLElement;
    const qcPanel = document.getElementById('quick-connect-panel') as HTMLElement;

    switch (status) {
        case ConnectionStatus.Streaming:
            canvas.classList.add('streaming');
            welcomeScreen.style.display = 'none';
            showConnectingOverlay(false);
            break;

        case ConnectionStatus.Connected:
            canvas.classList.remove('streaming');
            welcomeScreen.style.display = 'flex';
            if (qcPanel) qcPanel.style.opacity = '1';
            showConnectingOverlay(false);
            break;

        case ConnectionStatus.Disconnected:
            canvas.classList.remove('streaming');
            welcomeScreen.style.display = 'flex';
            if (qcPanel) qcPanel.style.opacity = '0.5';
            break;

        case ConnectionStatus.Reconnecting:
            showConnectingOverlay(true, 'מתחבר מחדש...');
            break;

        case ConnectionStatus.Error:
            showConnectingOverlay(false);
            break;
    }
}

function handleAgentListUpdated(agents: AgentInfo[]): void {
    console.log('Agents updated:', agents.length);
    toolbar.updateAgents(agents);
}

async function handleAgentSelect(agentId: string): Promise<void> {
    state.selectedAgentId = agentId;
    showConnectingOverlay(true, 'מתחבר למחשב המרוחק...');

    try {
        await client.startSession(agentId);
    } catch (error) {
        toast.show('שגיאה בהתחברות למחשב', 'error');
        showConnectingOverlay(false);
    }
}

async function handleDisconnect(): Promise<void> {
    try {
        await client.endSession();
        toast.show('החיבור נותק', 'info');
    } catch (error) {
        console.error('Disconnect error:', error);
    }
}

function handleSessionStarted(session: any): void {
    console.log('Session started:', session);
    showConnectingOverlay(false);

    const mode = client.isUsingP2P ? 'P2P (מהיר)' : 'SignalR';
    toast.show(`מחובר במצב ${mode}`, 'success');

    // Save to connection history
    if (state.selectedAgentId) {
        const agent = client.getAgents().find(a => a.agentId === state.selectedAgentId);
        saveConnectionToHistory(
            agent?.persistentAgentId || state.selectedAgentId,
            agent?.machineName || 'Unknown'
        );
    }
}

function handleSessionEnded(reason: string): void {
    console.log('Session ended:', reason);
    toast.show(`החיבור הסתיים: ${reason}`, 'info');
}

function handleMonitorSelect(index: number): void {
    toolbar.setMonitorSwitching(true);
    client.switchMonitor(index);
    // Timeout fallback in case MonitorChanged callback doesn't arrive
    setTimeout(() => toolbar.setMonitorSwitching(false), 5000);
}

function handleFullscreen(): void {
    if (document.fullscreenElement) {
        document.exitFullscreen();
        document.body.classList.remove('fullscreen');
    } else {
        document.documentElement.requestFullscreen();
        document.body.classList.add('fullscreen');
    }
}

function handleError(error: Error): void {
    console.error('Client error:', error);
    toast.show(error.message, 'error');
}

// ============================================================================
// Quick Connect (ID + Password)
// ============================================================================

function escapeHtml(str: string): string {
    return str.replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;').replace(/"/g, '&quot;').replace(/'/g, '&#39;');
}

function renderRecentConnections(): void {
    const container = document.getElementById('recent-connections');
    if (!container) return;

    const history = getConnectionHistory();
    if (history.length === 0) {
        container.innerHTML = '';
        return;
    }

    container.innerHTML = `
        <div class="rc-header">חיבורים אחרונים</div>
        <div class="rc-list">
            ${history.slice(0, 5).map(h => `
                <div class="rc-item" data-agent-id="${escapeHtml(h.agentId)}">
                    <button class="rc-fav-btn ${h.isFavorite ? 'active' : ''}" data-fav-id="${escapeHtml(h.agentId)}" title="מועדף">
                        ${h.isFavorite ? '★' : '☆'}
                    </button>
                    <span class="rc-name">${escapeHtml(h.machineName || h.agentId)}</span>
                    <span class="rc-id">${escapeHtml(h.agentId.substring(0, 12))}</span>
                    <button class="rc-connect-btn" data-rc-id="${escapeHtml(h.agentId)}" title="התחבר">→</button>
                </div>
            `).join('')}
        </div>
    `;

    // Attach event listeners
    container.querySelectorAll('.rc-connect-btn').forEach(btn => {
        btn.addEventListener('click', () => {
            const rcId = (btn as HTMLElement).dataset.rcId;
            if (rcId) {
                const agentIdInput = document.getElementById('qc-agent-id') as HTMLInputElement;
                agentIdInput.value = rcId;
                agentIdInput.dispatchEvent(new Event('input'));
                (document.getElementById('qc-password') as HTMLInputElement)?.focus();
            }
        });
    });

    container.querySelectorAll('.rc-fav-btn').forEach(btn => {
        btn.addEventListener('click', (e) => {
            e.stopPropagation();
            const favId = (btn as HTMLElement).dataset.favId;
            if (favId) {
                toggleFavorite(favId);
                renderRecentConnections();
            }
        });
    });
}

function setupQuickConnect(): void {
    const agentIdInput = document.getElementById('qc-agent-id') as HTMLInputElement;
    const passwordInput = document.getElementById('qc-password') as HTMLInputElement;
    const connectBtn = document.getElementById('qc-connect-btn') as HTMLButtonElement;
    const errorEl = document.getElementById('qc-error') as HTMLElement;

    // Enable connect button when both fields have values
    const updateButtonState = () => {
        const hasId = agentIdInput.value.trim().length > 0;
        const hasPassword = passwordInput.value.length > 0;
        connectBtn.disabled = !hasId || !hasPassword;
    };

    agentIdInput.addEventListener('input', () => { errorEl.style.display = 'none'; updateButtonState(); });
    passwordInput.addEventListener('input', () => { errorEl.style.display = 'none'; updateButtonState(); });

    // Enter key to connect
    passwordInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter' && !connectBtn.disabled) {
            handleQuickConnect();
        }
    });
    agentIdInput.addEventListener('keydown', (e) => {
        if (e.key === 'Enter') {
            passwordInput.focus();
        }
    });

    connectBtn.addEventListener('click', handleQuickConnect);

    // Render recent connections
    renderRecentConnections();
}

async function handleQuickConnect(): Promise<void> {
    const agentIdInput = document.getElementById('qc-agent-id') as HTMLInputElement;
    const passwordInput = document.getElementById('qc-password') as HTMLInputElement;
    const connectBtn = document.getElementById('qc-connect-btn') as HTMLButtonElement;
    const errorEl = document.getElementById('qc-error') as HTMLElement;

    const agentId = agentIdInput.value.trim();
    const password = passwordInput.value;

    if (!agentId || !password) return;

    // Show loading state
    connectBtn.disabled = true;
    connectBtn.innerHTML = '<span class="spinner-small"></span> מתחבר...';
    errorEl.style.display = 'none';
    showConnectingOverlay(true, 'מתחבר למחשב המרוחק...');

    try {
        await client.startSessionWithCredentials(agentId, password);
        // Success — session started, UI updated by event handlers
        passwordInput.value = ''; // Clear password for security
        saveConnectionToHistory(agentId, agentId); // Save to recent connections
    } catch (error: any) {
        showConnectingOverlay(false);
        const message = error?.message || 'שגיאה בהתחברות';

        // Map common errors to Hebrew messages
        let hebrewError = message;
        if (message.includes('not found') || message.includes('not connected')) {
            hebrewError = 'מחשב לא נמצא או לא מחובר';
        } else if (message.includes('password') || message.includes('credentials') || message.includes('Invalid')) {
            hebrewError = 'מזהה או סיסמה שגויים';
        } else if (message.includes('locked') || message.includes('Locked')) {
            hebrewError = 'החשבון נעול עקב ניסיונות כושלים. נסה שוב מאוחר יותר';
        } else if (message.includes('busy') || message.includes('unavailable')) {
            hebrewError = 'המחשב תפוס כרגע. נסה שוב מאוחר יותר';
        } else if (message.includes('בדוק מזהה')) {
            hebrewError = message; // Already Hebrew
        }

        errorEl.textContent = hebrewError;
        errorEl.style.display = 'block';
        toast.show(hebrewError, 'error');
    } finally {
        connectBtn.disabled = false;
        connectBtn.innerHTML = '<span class="qc-btn-icon">🔗</span><span class="qc-btn-text">התחבר</span>';
    }
}

// ============================================================================
// Connection Status Banner
// ============================================================================

function updateConnectionBanner(status: ConnectionStatus): void {
    const banner = document.getElementById('connection-status-banner') as HTMLElement;
    if (!banner) return;

    const dot = banner.querySelector('.csb-dot') as HTMLElement;
    const text = banner.querySelector('.csb-text') as HTMLElement;

    // Remove previous state classes
    banner.className = 'connection-status-banner';

    switch (status) {
        case ConnectionStatus.Disconnected:
            banner.classList.add('csb-disconnected');
            text.textContent = 'מנותק מהשרת';
            break;
        case ConnectionStatus.Connecting:
            banner.classList.add('csb-connecting');
            text.textContent = 'מתחבר לשרת...';
            break;
        case ConnectionStatus.Connected:
            banner.classList.add('csb-connected');
            text.textContent = 'מחובר — בחר מחשב או הזן מזהה';
            break;
        case ConnectionStatus.Streaming:
            banner.classList.add('csb-streaming');
            text.textContent = client.isUsingP2P ? 'חיבור ישיר P2P — זמן אמת' : 'מחובר למחשב מרוחק';
            break;
        case ConnectionStatus.Reconnecting:
            banner.classList.add('csb-reconnecting');
            text.textContent = 'מתחבר מחדש...';
            break;
        case ConnectionStatus.Error:
            banner.classList.add('csb-error');
            text.textContent = 'שגיאת חיבור';
            break;
    }
}

// ============================================================================
// UI Helpers
// ============================================================================

function showConnectingOverlay(show: boolean, text: string = 'מתחבר...'): void {
    const overlay = document.getElementById('connecting-overlay') as HTMLElement;
    const textEl = overlay.querySelector('.connecting-text') as HTMLElement;

    if (textEl) {
        textEl.textContent = text;
    }

    overlay.style.display = show ? 'flex' : 'none';
}

// ============================================================================
// Performance Overlay
// ============================================================================

let perfOverlayVisible = false;

function togglePerformanceOverlay(): void {
    perfOverlayVisible = !perfOverlayVisible;
    let overlay = document.getElementById('perf-overlay');

    if (!perfOverlayVisible) {
        overlay?.remove();
        return;
    }

    if (!overlay) {
        overlay = document.createElement('div');
        overlay.id = 'perf-overlay';
        overlay.className = 'perf-overlay';
        document.getElementById('canvas-container')?.appendChild(overlay);
    }

    updatePerfOverlay();
}

function updatePerfOverlay(): void {
    if (!perfOverlayVisible) return;
    const overlay = document.getElementById('perf-overlay');
    if (!overlay) return;

    const stats = (window as any).LEVOR?.client;
    const mode = stats?.isUsingP2P ? 'P2P' : 'Relay';
    const encrypted = stats?.isEncrypted ? 'E2E' : 'None';

    overlay.innerHTML = `
        <div class="perf-title">Performance</div>
        <div class="perf-row">Mode: <span>${mode}</span></div>
        <div class="perf-row">Encryption: <span>${encrypted}</span></div>
        <div class="perf-row">Resolution: <span id="perf-res">--</span></div>
        <div class="perf-row">FPS: <span id="perf-fps">--</span></div>
        <div class="perf-row">Bitrate: <span id="perf-bitrate">--</span></div>
        <div class="perf-row">Latency: <span id="perf-latency">--</span></div>
        <div class="perf-row">Decode: <span id="perf-decode">--</span></div>
    `;

    requestAnimationFrame(updatePerfOverlay);
}

// ============================================================================
// Keyboard Shortcuts
// ============================================================================

document.addEventListener('keydown', (e) => {
    // F9 - Toggle AI Sidebar
    if (e.key === 'F9') {
        e.preventDefault();
        aiSidebar.toggle();
    }

    // F11 - Fullscreen
    if (e.key === 'F11') {
        e.preventDefault();
        handleFullscreen();
    }

    // Escape - Exit fullscreen or release pointer lock
    if (e.key === 'Escape') {
        if (document.fullscreenElement) {
            document.exitFullscreen();
            document.body.classList.remove('fullscreen');
        }
    }

    // Ctrl+Shift+P — Performance overlay
    if (e.ctrlKey && e.shiftKey && e.key === 'P') {
        e.preventDefault();
        togglePerformanceOverlay();
    }

    // Ctrl+Shift+D — Disconnect
    if (e.ctrlKey && e.shiftKey && e.key === 'D') {
        e.preventDefault();
        handleDisconnect();
    }
});

// ============================================================================
// AI Features
// ============================================================================

async function handleAnalyzeScreen(): Promise<void> {
    aiSidebar.open();
    aiSidebar.addExternalMessage('user', 'Analyze the current screen');

    try {
        const response = await fetch(`${CONFIG.serverUrl}/api/ai/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt: 'Please analyze the current remote desktop screen and describe what you see. Provide any relevant observations about the system state.',
                systemPrompt: 'You are helping with a remote desktop session. Describe what might be visible on the screen and suggest any actions.'
            })
        });

        if (response.ok) {
            const data = await response.json();
            aiSidebar.addExternalMessage('ai', data.content || data.response || 'No analysis available.');
        } else {
            aiSidebar.addExternalMessage('ai', `Screen analysis is not available. The AI endpoint returned status ${response.status}.`);
        }
    } catch (error: any) {
        aiSidebar.addExternalMessage('ai', `Error requesting screen analysis: ${error.message || 'Could not reach the server.'}`);
    }
}

/**
 * Show AI-powered error resolution modal when connection fails
 */
async function showAIErrorResolution(errorMessage: string): Promise<void> {
    // Create modal overlay
    const existingModal = document.getElementById('ai-error-modal');
    if (existingModal) existingModal.remove();

    const modal = document.createElement('div');
    modal.id = 'ai-error-modal';
    modal.innerHTML = `
        <div class="ai-error-overlay">
            <div class="ai-error-dialog">
                <div class="ai-error-header">
                    <span class="ai-error-icon">&#x1F916;</span>
                    <span class="ai-error-title">AI Error Resolution</span>
                    <button class="ai-error-close">&times;</button>
                </div>
                <div class="ai-error-body">
                    <div class="ai-error-original">
                        <strong>Error:</strong> ${errorMessage.replace(/</g, '&lt;').replace(/>/g, '&gt;')}
                    </div>
                    <div class="ai-error-loading">
                        <span class="ai-dot"></span>
                        <span class="ai-dot"></span>
                        <span class="ai-dot"></span>
                        <span style="margin-left: 8px; color: #8899AA;">Analyzing error...</span>
                    </div>
                    <div class="ai-error-solution" style="display: none;"></div>
                </div>
            </div>
        </div>
    `;

    // Inject modal styles
    if (!document.getElementById('ai-error-modal-styles')) {
        const style = document.createElement('style');
        style.id = 'ai-error-modal-styles';
        style.textContent = `
            .ai-error-overlay {
                position: fixed; top: 0; left: 0; width: 100vw; height: 100vh;
                background: rgba(0,0,0,0.6); z-index: 99999;
                display: flex; align-items: center; justify-content: center;
                animation: ai-fade-in 0.2s ease;
            }
            .ai-error-dialog {
                background: #141E2A; border: 1px solid #1C2938; border-radius: 16px;
                width: 500px; max-width: 90vw; max-height: 80vh; overflow-y: auto;
                box-shadow: 0 20px 60px rgba(0,0,0,0.5);
            }
            .ai-error-header {
                display: flex; align-items: center; gap: 10px;
                padding: 16px 20px; border-bottom: 1px solid #1C2938;
            }
            .ai-error-icon { font-size: 20px; }
            .ai-error-title { color: white; font-size: 15px; font-weight: 600; flex: 1; }
            .ai-error-close {
                width: 28px; height: 28px; border: none; background: transparent;
                color: #8899AA; font-size: 18px; cursor: pointer; border-radius: 6px;
                display: flex; align-items: center; justify-content: center;
            }
            .ai-error-close:hover { background: #1C2938; color: white; }
            .ai-error-body { padding: 20px; }
            .ai-error-original {
                background: #0F1923; border: 1px solid #2A1A1A; border-radius: 8px;
                padding: 12px; color: #FF6B6B; font-size: 13px; margin-bottom: 16px;
            }
            .ai-error-loading {
                display: flex; align-items: center; padding: 12px 0;
            }
            .ai-error-solution {
                background: #0F1923; border: 1px solid #1C2938; border-radius: 8px;
                padding: 16px; color: #C0D0E0; font-size: 13px; line-height: 1.6;
            }
            .ai-error-solution strong { color: #00BF63; }
            .ai-error-solution code {
                background: #1C2938; padding: 1px 4px; border-radius: 3px;
                font-family: Consolas, monospace; font-size: 12px;
            }
            @keyframes ai-fade-in { from { opacity: 0; } to { opacity: 1; } }
        `;
        document.head.appendChild(style);
    }

    document.body.appendChild(modal);

    // Close handlers
    modal.querySelector('.ai-error-close')?.addEventListener('click', () => modal.remove());
    modal.querySelector('.ai-error-overlay')?.addEventListener('click', (e) => {
        if ((e.target as HTMLElement).classList.contains('ai-error-overlay')) modal.remove();
    });

    // Fetch AI troubleshooting
    try {
        const response = await fetch(`${CONFIG.serverUrl}/api/ai/chat`, {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
                prompt: `I got this error while using a remote desktop application: "${errorMessage}". Please provide step-by-step troubleshooting instructions.`,
                systemPrompt: 'You are an IT support assistant. Provide clear, numbered troubleshooting steps. Be concise.'
            })
        });

        const loadingEl = modal.querySelector('.ai-error-loading') as HTMLElement;
        const solutionEl = modal.querySelector('.ai-error-solution') as HTMLElement;

        if (response.ok) {
            const data = await response.json();
            const solution = data.content || data.response || 'No troubleshooting steps available.';
            loadingEl.style.display = 'none';
            solutionEl.style.display = 'block';
            // Basic markdown-like rendering
            solutionEl.innerHTML = solution
                .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
                .replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>')
                .replace(/`(.*?)`/g, '<code>$1</code>')
                .replace(/\n/g, '<br>');
        } else {
            loadingEl.style.display = 'none';
            solutionEl.style.display = 'block';
            solutionEl.innerHTML = `<strong>Manual Troubleshooting Steps:</strong><br><br>
                1. Check your network connection<br>
                2. Verify the server URL is correct<br>
                3. Ensure the remote desktop server is running<br>
                4. Try refreshing the page<br>
                5. Check if a firewall is blocking the connection`;
        }
    } catch {
        const loadingEl = modal.querySelector('.ai-error-loading') as HTMLElement;
        const solutionEl = modal.querySelector('.ai-error-solution') as HTMLElement;
        if (loadingEl) loadingEl.style.display = 'none';
        if (solutionEl) {
            solutionEl.style.display = 'block';
            solutionEl.innerHTML = `<strong>Manual Troubleshooting Steps:</strong><br><br>
                1. Check your network connection<br>
                2. Verify the server URL is correct<br>
                3. Ensure the remote desktop server is running<br>
                4. Try refreshing the page<br>
                5. Check if a firewall is blocking the connection`;
        }
    }
}

// ============================================================================
// Start Application
// ============================================================================

// Wait for DOM to be ready
if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', initializeApp);
} else {
    initializeApp();
}

// Export for debugging (will be populated after init)
(window as any).LEVOR = {
    get client() { return client; },
    get toolbar() { return toolbar; },
    get aiSidebar() { return aiSidebar; },
    state,
    CONFIG
};
